/**
 * useDesktop() — thin hook wrapping `window.akomodaDesktop`.
 * When the app runs in a normal browser, methods degrade gracefully.
 *
 * Usage:
 *   const desktop = useDesktop();
 *   if (desktop.isDesktop) desktop.notify({ title: "New booking", body: "Room 205 · 3 nights" });
 *   desktop.printReceipt({ silent: true });
 */
import { useEffect, useState, useCallback } from "react";

const shim = {
    isDesktop: false,
    platform: null,
    notify: async ({ title, body } = {}) => {
        // Fall back to the Web Notifications API when running in a browser
        if (!("Notification" in window)) return { ok: false };
        if (Notification.permission === "default") await Notification.requestPermission();
        if (Notification.permission === "granted") {
            const n = new Notification(title || "Akomoda", { body });
            n.onclick = () => window.focus();
            return { ok: true };
        }
        return { ok: false };
    },
    printReceipt: async () => { window.print(); return { ok: true }; },
    listPrinters: async () => [],
    setPosPrinter: async () => ({ ok: false }),
    getSettings: async () => ({ isDesktop: false }),
    setSettings: async () => ({ ok: false }),
    checkForUpdates: async () => ({ ok: false, reason: "browser" }),
    on: () => () => {},
};

export function useDesktop() {
    const [desktop] = useState(() => (typeof window !== "undefined" && window.akomodaDesktop) || shim);
    const [version, setVersion] = useState(null);

    useEffect(() => {
        if (!desktop.isDesktop) return;
        const off = desktop.on("akomoda:version", (v) => setVersion(v));
        return off;
    }, [desktop]);

    return { ...desktop, version };
}

/**
 * Send a native notification when the app receives a new-reservation realtime event.
 * Wire this once from a top-level component (e.g. AppLayout).
 */
export function useNativeReservationNotifications() {
    const desktop = useDesktop();
    const notify = useCallback((payload) => {
        if (!payload) return;
        const guest = payload.guest_name || "New guest";
        const dates = payload.check_in ? `${payload.check_in} → ${payload.check_out}` : "";
        desktop.notify({
            title: `New reservation · ${guest}`,
            body: dates || `Booking ${payload.confirmation_code || ""} received`,
            urgency: "normal",
        });
    }, [desktop]);
    return notify;
}
